import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { IPagedResponse } from "shared/interfaces/solicitacao";

export interface ILiberacaoTipologia {
  id: number;
  empresaId: number;
  empresaNome: string;
  projetistaId: number;
  projetistaNome: string;
  linhaId?: number;
  linhaNome?: string;
  situacao: string;
  dataLiberacao: string;
  observacoes?: string;
  sincronizadoLegado: boolean;
  dataSincronizacaoLegado?: string;
}

export interface ICriarLiberacaoTipologia {
  empresaId: number;
  idSerial?: number;
  projetistaId: number;
  linhaId?: number;
  situacao: string;
  observacoes?: string;
}

export interface ICriarLiberacaoReferencia {
  empresaId: number;
  idSerial?: number;
  referenciaId: number;
  linhaId?: number;
  situacao: string;
  versaoAtual?: string;
}

export interface ICriarLiberacaoAplicabilidade {
  empresaId: number;
  idSerial?: number;
  aplicabilidadeId: number;
  situacao: string;
}

export interface ICriarLiberacaoTabelaPreco {
  empresaId: number;
  idSerial?: number;
  tabelaPrecoId: number;
  situacao: string;
  observacoes?: string;
}

export interface ILiberacaoReferencia {
  id: number;
  empresaId: number;
  empresaNome: string;
  referenciaId: number;
  referenciaCodigo: string;
  linhaId?: number;
  linhaNome?: string;
  situacao: string;
  dataLiberacao: string;
  versaoAtual?: string;
  sincronizadoLegado: boolean;
  dataSincronizacaoLegado?: string;
}

export interface ILiberacaoAplicabilidade {
  id: number;
  empresaId: number;
  empresaNome: string;
  aplicabilidadeId: number;
  aplicabilidadeDescricao: string;
  situacao: string;
  dataLiberacao: string;
  sincronizadoLegado: boolean;
  dataSincronizacaoLegado?: string;
}

export interface ILiberacaoTabelaPreco {
  id: number;
  empresaId: number;
  empresaNome: string;
  tabelaPrecoId: number;
  tabelaPrecoNome: string;
  tipoMaterial?: string;
  situacao: string;
  dataLiberacao: string;
  observacoes?: string;
  sincronizadoLegado: boolean;
  dataSincronizacaoLegado?: string;
}

export interface IListarLiberacoesTipologiaParams {
  empresaId?: number;
  idSerial?: number;
  projetistaId?: number;
  linhaId?: number;
  page?: number;
  limit?: number;
}

export interface IListarLiberacoesReferenciaParams {
  empresaId?: number;
  idSerial?: number;
  referenciaId?: number;
  linhaId?: number;
  page?: number;
  limit?: number;
}

export interface IListarLiberacoesAplicabilidadeParams {
  empresaId?: number;
  idSerial?: number;
  aplicabilidadeId?: number;
  page?: number;
  limit?: number;
}

export interface IListarLiberacoesTabelaPrecoParams {
  empresaId?: number;
  idSerial?: number;
  tabelaPrecoId?: number;
  page?: number;
  limit?: number;
}

export const listarLiberacoesTipologia = async (
  params: IListarLiberacoesTipologiaParams
) => {
  try {
    const { data } = await api.get<IPagedResponse<ILiberacaoTipologia>>(
      "/Liberacao/Tipologia",
      {
        params: {
          empresaId: params.empresaId,
          idSerial: params.idSerial,
          projetistaId: params.projetistaId,
          linhaId: params.linhaId,
          page: params.page || 1,
          limit: params.limit || 10,
        },
      }
    );
    return data ?? {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar as liberações de tipologias";

    getErrorsByApi(error, errorMessage);
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  }
};

export const criarLiberacaoTipologia = async (
  dados: ICriarLiberacaoTipologia
) => {
  try {
    const { data } = await api.post("/Liberacao/Tipologia", dados);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao criar a liberação de tipologia";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const removerLiberacaoTipologia = async (id: number) => {
  try {
    const { data } = await api.delete(`/Liberacao/Tipologia/${id}`);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao remover a liberação de tipologia";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const listarLiberacoesReferencia = async (
  params: IListarLiberacoesReferenciaParams
) => {
  try {
    const { data } = await api.get<IPagedResponse<ILiberacaoReferencia>>(
      "/Liberacao/Referencia",
      {
        params: {
          empresaId: params.empresaId,
          idSerial: params.idSerial,
          referenciaId: params.referenciaId,
          linhaId: params.linhaId,
          page: params.page || 1,
          limit: params.limit || 10,
        },
      }
    );
    return data ?? {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar as liberações de referências";

    getErrorsByApi(error, errorMessage);
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  }
};

// Plano 22/10/2026 (rev. 2, Frente 4): o que o serial tem na base antiga (CEM Desktop antigo),
// só para consulta nas abas Cadastros N1 (nivel 1) e N2 (nivel 2).
export interface ILiberacaoBaseAntiga {
  id: number;
  tipoPacote: string;
  projetista: string;
  referencia: string;
  linha: string;
  acesso: string;
  dataAtualizacaoPacote?: string | null;
}

export const listarLiberacoesBaseAntiga = async (idSerial: number, nivel: 1 | 2): Promise<ILiberacaoBaseAntiga[]> => {
  try {
    const { data } = await api.get<ILiberacaoBaseAntiga[]>("/Liberacao/BaseAntiga", { params: { idSerial, nivel } });
    return data ?? [];
  } catch (error: any) {
    throw (
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Não foi possível carregar o que o cliente tem no CEM Desktop antigo."
    );
  }
};

export const criarLiberacaoReferencia = async (
  dados: ICriarLiberacaoReferencia
) => {
  try {
    const { data } = await api.post("/Liberacao/Referencia", dados);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao criar a liberação de referência";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const removerLiberacaoReferencia = async (id: number) => {
  try {
    const { data } = await api.delete(`/Liberacao/Referencia/${id}`);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao remover a liberação de referência";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const listarLiberacoesAplicabilidade = async (
  params: IListarLiberacoesAplicabilidadeParams
) => {
  try {
    const { data } = await api.get<IPagedResponse<ILiberacaoAplicabilidade>>(
      "/Liberacao/Aplicabilidade",
      {
        params: {
          empresaId: params.empresaId,
          idSerial: params.idSerial,
          aplicabilidadeId: params.aplicabilidadeId,
          page: params.page || 1,
          limit: params.limit || 10,
        },
      }
    );
    return data ?? {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar as liberações de aplicabilidades";

    getErrorsByApi(error, errorMessage);
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  }
};

export const criarLiberacaoAplicabilidade = async (
  dados: ICriarLiberacaoAplicabilidade
) => {
  try {
    const { data } = await api.post("/Liberacao/Aplicabilidade", dados);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao criar a liberação de aplicabilidade";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const removerLiberacaoAplicabilidade = async (id: number) => {
  try {
    const { data } = await api.delete(`/Liberacao/Aplicabilidade/${id}`);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao remover a liberação de aplicabilidade";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const listarLiberacoesTabelaPreco = async (
  params: IListarLiberacoesTabelaPrecoParams
) => {
  try {
    const { data } = await api.get<IPagedResponse<ILiberacaoTabelaPreco>>(
      "/Liberacao/TabelaPreco",
      {
        params: {
          empresaId: params.empresaId,
          idSerial: params.idSerial,
          tabelaPrecoId: params.tabelaPrecoId,
          page: params.page || 1,
          limit: params.limit || 10,
        },
      }
    );
    return data ?? {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar as liberações de tabelas de preço";

    getErrorsByApi(error, errorMessage);
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  }
};

export const criarLiberacaoTabelaPreco = async (
  dados: ICriarLiberacaoTabelaPreco
) => {
  try {
    const { data } = await api.post("/Liberacao/TabelaPreco", dados);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao criar a liberação de tabela de preços";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const removerLiberacaoTabelaPreco = async (id: number) => {
  try {
    const { data } = await api.delete(`/Liberacao/TabelaPreco/${id}`);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao remover a liberação de tabela de preços";

    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export interface IMaterialDisponivelDelphi {
  id: string;
  tipoPacote: string;
  referencia: string;
  linha: string;
  data: string;
}

export interface ITipologiaDisponivelDelphi {
  id: string;
  projetista: string;
  linha: string;
  data: string;
}

export interface IAplicabilidadeDisponivelDelphi {
  id: string;
  referencia: string;
  data: string;
}

export interface ITabelaPrecoDisponivelDelphi {
  id: string;
  descricao: string;
  data: string;
  empresa: string;
  tipoMaterial: string;
}

export const listarMateriaisDisponiveisN1 = async (params: {
  empresaId?: number;
  idSerial?: number;
  situacao?: string;
}): Promise<IMaterialDisponivelDelphi[]> => {
  try {
    const { data } = await api.get<IMaterialDisponivelDelphi[]>(
      "/Liberacao/Referencia/Disponiveis",
      { params }
    );
    return data ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Erro ao carregar materiais disponíveis";
    getErrorsByApi(error, errorMessage);
    return [];
  }
};

export const listarTipologiasDisponiveisN2 = async (params: {
  empresaId?: number;
  idSerial?: number;
  situacao?: string;
}): Promise<ITipologiaDisponivelDelphi[]> => {
  try {
    const { data } = await api.get<ITipologiaDisponivelDelphi[]>(
      "/Liberacao/Tipologia/Disponiveis",
      { params }
    );
    return data ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Erro ao carregar tipologias disponíveis";
    getErrorsByApi(error, errorMessage);
    return [];
  }
};

export const listarAplicabilidadesDisponiveis = async (params: {
  empresaId?: number;
  idSerial?: number;
  situacao?: string;
}): Promise<IAplicabilidadeDisponivelDelphi[]> => {
  try {
    const { data } = await api.get<IAplicabilidadeDisponivelDelphi[]>(
      "/Liberacao/Aplicabilidade/Disponiveis",
      { params }
    );
    return data ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Erro ao carregar aplicabilidades disponíveis";
    getErrorsByApi(error, errorMessage);
    return [];
  }
};

export const listarTabelasPrecoDisponiveis = async (params: {
  empresaId?: number;
  idSerial?: number;
  situacao?: string;
}): Promise<ITabelaPrecoDisponivelDelphi[]> => {
  try {
    const { data } = await api.get<ITabelaPrecoDisponivelDelphi[]>(
      "/Liberacao/TabelaPreco/Disponiveis",
      { params }
    );
    return data ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Erro ao carregar tabelas de preço disponíveis";
    getErrorsByApi(error, errorMessage);
    return [];
  }
};

